
import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { RouterModule } from "@angular/router";
import { BookingModule } from "@fry/booking/app";
import { FryCommonModule } from "@fry/components/common";
import { InteractiveTableModule } from "@fry/components/common/list/interactive-table";
import { InteractiveTableOptions } from "@fry/components/common/list/interactive-table/interactive-table.component";
import { MaterialModule } from "@fry/components/material";
import { ListDefaults, SearchModel, TableService } from "@fry/lib/list";
import { UnboxedModule } from "@fry/ui";
import { tap } from "rxjs";
import { ReactiveFormsModule } from "@angular/forms";
import { EasFieldType, EasFormGroup, EasFormService } from "@fry/components/common/easform";
import { EasFormModule } from "@fry/components/common/easform/easform.module";
import { BoardCard } from "@fry/boards/lib";
import { BookingAvailableCardOptions } from "./booking-available-card.interface";
import { BookingAvailableCardSourceFactory } from "./booking-available-card.source";
import { EasBuilderField, EasBuilderGroup } from "@fry/components/common/form-builder/builder.interfaces";
import { BuilderFormService } from "@fry/components/common/form-builder/form.service";

const DefaultSeachModel: SearchModel = {
    "filter": {
        "windowState": "window"
    },
    "sort": {},
    "select": ["venue", "date", "window", "status", "free", "price"],
};

@Component({
    selector: "eas-booking-available-edit-card",
    templateUrl: "./booking-available-card.settings.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    RouterModule,
    FryCommonModule,
    UnboxedModule,
    BookingModule,
    InteractiveTableModule,
    MaterialModule,
    ReactiveFormsModule,
    EasFormModule
],
    providers: [
        TableService
    ]
})
export class BookingAvailableSettingsComponent {

    dialogRef = inject(MatDialogRef<BookingAvailableSettingsComponent>);
    data: { options: BookingAvailableCardOptions, card: BoardCard } = inject(MAT_DIALOG_DATA);
    options = this.data.options;
    card = this.data.card;

    private tableService = inject(TableService);
    private sourceFactory = inject(BookingAvailableCardSourceFactory);

    private formService = inject(EasFormService);


    private builderService = inject(BuilderFormService);

    private builderFields: EasBuilderField[] = [
        {
            "id": "title",
            "fieldType": "string",
            "type": EasFieldType.Field,
            "options": {
                "label": $localize `Title`
            }
        },
        {
            "id": "bookingType",
            "fieldType": "multiSelect",
            "type": EasFieldType.Field,
            "options": {
                "label": $localize `Booking Type`,
                "categoriesSource": "/booking-types/stub",
                "key": "id"
            }
        },
        {
            "id": "hideControls",
            "fieldType": "boolean",
            "type": EasFieldType.Field,
            "options": {
                "label": $localize `Hide filtering and searching options for users`,
            }
        }
    ]

    public form!: EasFormGroup;

    $searchModel = this.tableService.$currentSearchModel;
    $fixedSearchModel: SearchModel = {
        filter: {
        },
        sort: { sort_on: 'order', sort_order: 'desc' },
        select: ["venue", "date", "window", "status", "free", "price"],
        options: {
            includeFormFields: false
        },
        context: {
            filter: {},
        },
    }

    tableLoaded = signal<boolean>(false);

    public tableComponentOptions: InteractiveTableOptions = {
        enableColumns: true,
        enableFilters: true,
        enableExpand: true,
        enableControls: true,
        expand: {
            label: $localize `Title`,
            type: "modal",
            modalType: "bookingApply"
        },
        layout: 'table'
      };

    constructor() {
    }

    ngOnInit() {
        const initial = {
            title: this.options.title,
            bookingType: this.options.fixedFilter?.bookingType,
            hideControls: this.options.options?.hideControls,
        }

        const form: EasBuilderGroup = {
            id: 'root',
            type: EasFieldType.Group,
            fieldType: 'group',
            fields: this.builderFields
        };

        if (this.$fixedSearchModel.context !== undefined) {
            this.$fixedSearchModel.context.extra = { cardData: this.options };
        }

        this.builderService.buildForm(form, { user: '__bulk__' }).subscribe(form => {
            this.form = this.formService.createForm(form, initial);
            this.form.control.valueChanges.subscribe(() => {
                if (this.$fixedSearchModel.context !== undefined) {
                    this.$fixedSearchModel.context.extra = {cardData: this.getOptions()};
                }
                this.tableService.updateFixedSearchModel(this.$fixedSearchModel);
                this.tableService.reload().subscribe();
            });

            this.initializeTable().subscribe({
                error: (error) => {
                    console.error(error);
                }
            });
        })
    }



    initializeTable() {
        const pageSize = 1000;
        const defaults: ListDefaults = { listType: 'table', engine: 'es', pageSize: pageSize };
        defaults.hideFilters = ['bookingType', 'text'];
        defaults.fixedSearchModel = this.$fixedSearchModel;
        defaults.quickFilters = ['windowState'];
        defaults.searchModel = this.options.searchModel || JSON.parse(JSON.stringify(DefaultSeachModel));
        const source = this.sourceFactory.getTableSource(this.card.boardId, this.card.id);
        return this.tableService.setupWith(source, defaults, 'general')
            .pipe(tap(() => this.tableLoaded.set(true)));
    }

    onCancel() {
        this.dialogRef.close();
    }

    getOptions(): BookingAvailableCardOptions {
        const searchModel = this.$searchModel();
        if (searchModel === undefined) {
            throw new Error('Search model is undefined');
        }

        const value = this.form.getValue();
        const options: BookingAvailableCardOptions = {
            title: value.title,
            options: {
                hideControls: value.hideControls
            },
            fixedFilter: {
                bookingType: value.bookingType
            },
            searchModel,
        }
        return options;
    };

    onSave() {
        if (this.form.control.invalid) {
            return;
        }

        const options = this.getOptions()

        this.dialogRef.close(options);
    }

    onUpdate() {
        this.initializeTable().subscribe({
            error: (error) => {
                console.error(error);
            }
        });
    }
}
